import React, { useState, useMemo, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useChallengeData } from '../../utils/fetchChallengeData';
import { sportColors } from '../../utils/sports';
import { FaFutbol, FaCheck, FaTimes, FaChartLine, FaInfoCircle, FaAngleLeft, FaAngleRight } from 'react-icons/fa';
import { FaBasketball, FaVolleyball } from 'react-icons/fa6';
import { GiHockey } from 'react-icons/gi';
import { MdOutlineSportsHandball, MdSportsMma } from 'react-icons/md';
import { IoTennisball } from 'react-icons/io5';
import { IoIosFootball } from 'react-icons/io';
import { SiCounterstrike } from 'react-icons/si';
import { CgMoreO } from 'react-icons/cg';
import { PiBoxingGlove, PiSoccerBallDuotone } from 'react-icons/pi';
import PageHeader from '../../components/PageHeader/PageHeader';
import { SkeletonBetList } from '../../components/Skeleton/Skeleton';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import './HistoricoPage.css';

// Formata data para dd-mm-yyyy
const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
        if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
            const [datePart] = dateStr.split('T');
            const [year, month, day] = datePart.split('-');
            return `${day}-${month}-${year}`;
        }
        if (typeof dateStr === 'string' && dateStr.includes('/')) {
            return dateStr.replace(/\//g, '-');
        }
        return String(dateStr);
    } catch (e) {
        return String(dateStr);
    }
};

// Parse date string to Date object (handles YYYY-MM-DD and DD/MM/YYYY)
const parseDate = (dateStr) => {
    if (!dateStr) return new Date(0);
    // YYYY-MM-DD format
    if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
        const [datePart] = dateStr.split('T');
        const [year, month, day] = datePart.split('-').map(Number);
        return new Date(year, month - 1, day);
    }
    // DD/MM/YYYY format
    if (typeof dateStr === 'string' && dateStr.includes('/')) {
        const [day, month, year] = dateStr.split('/').map(Number);
        return new Date(year, month - 1, day);
    }
    return new Date(dateStr);
};

function HistoricoPage() {
  const { challengeHistory, loading, error } = useChallengeData();
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [isAnimating, setIsAnimating] = useState(false);
  const itemsPerPage = 5;

  const apostas = useMemo(() => {
    if (!challengeHistory || challengeHistory.length === 0) return [];
    return challengeHistory
      .filter(ch => ch && ch.games && Array.isArray(ch.games) && ch.games.length > 0)
      .map((ch) => ({
        id: ch.id,
        date: ch.date,
        type: ch.games.length > 1 ? 'Múltipla' : 'Simples',
        gamesCount: ch.games.length,
        totalOdd: ch.totalOdd,
        amount: ch.amount,
        potentialWinnings: Number((ch.amount * ch.totalOdd).toFixed(2)),
        status: ch.result || 'pending',
        games: ch.games.map((g) => ({
          homeTeam: g.homeTeam,
          awayTeam: g.awayTeam,
          odd: g.gameOdd,
          sport: g.sport,
          pick: g.pick,
        })),
      }));
  }, [challengeHistory]);

  const totalPages = Math.ceil(apostas.length / itemsPerPage);
  const paginatedApostas = apostas.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  useEffect(() => {
    setIsAnimating(true);
    const timer = setTimeout(() => setIsAnimating(false), 300);
    return () => clearTimeout(timer);
  }, [currentPage]);

  const { balance } = useMemo(() => {
    let currentBalance = 10;
    const sortedApostas = apostas
      .filter(aposta => aposta.status !== 'pending')
      .sort((a, b) => parseDate(a.date) - parseDate(b.date));
    sortedApostas.forEach(aposta => {
      if (aposta.status === 'won') {
        currentBalance += aposta.potentialWinnings - aposta.amount;
      } else if (aposta.status === 'lost') {
        currentBalance -= aposta.amount;
        currentBalance = Math.max(10, currentBalance);
      }
    });
    return { balance: currentBalance };
  }, [apostas]);

  const totalAdded = useMemo(() => {
    const sum = challengeHistory.reduce((sum, ch) => {
      console.log('Desafio:', ch.id, 'addedAmount:', ch.addedAmount, 'tipo:', typeof ch.addedAmount);
      const newSum = sum + (ch.addedAmount || 0);
      console.log('Sum até agora:', newSum);
      return newSum;
    }, 0);
    console.log('Total Added final:', sum);
    return sum;
  }, [challengeHistory]);

  const chartData = useMemo(() => {
    let currentBalance = 10;
    const sortedApostas = apostas
      .filter(aposta => aposta.status !== 'pending')
      .sort((a, b) => parseDate(a.date) - parseDate(b.date));
    
    const groupedByDate = sortedApostas.reduce((acc, aposta) => {
      if (!acc[aposta.date]) {
        acc[aposta.date] = [];
      }
      acc[aposta.date].push(aposta);
      return acc;
    }, {});
    
    const data = [];
    Object.keys(groupedByDate).sort((a, b) => parseDate(a) - parseDate(b)).forEach(date => {
      const dayApostas = groupedByDate[date];
      dayApostas.forEach(aposta => {
        if (aposta.status === 'won') {
          currentBalance += aposta.potentialWinnings - aposta.amount;
        } else if (aposta.status === 'lost') {
          currentBalance -= aposta.amount;
          if (currentBalance < 10) {
            currentBalance = 10;
          }
        }
      });
      const dateObj = parseDate(date);
      data.push({ date: dateObj.getTime(), balance: Number(currentBalance.toFixed(2)) });
    });

    const initialPoint = { date: new Date(2026, 0, 14).getTime(), balance: 10 };
    return [initialPoint, ...data];
  }, [apostas]);

  const wonCount = apostas.filter(aposta => aposta.status === 'won').length;
  const lostCount = apostas.filter(aposta => aposta.status === 'lost').length;
  const nextBet = apostas.length > 0 && apostas[0] && apostas[0].status !== 'pending' ? (balance <= 10 ? balance : balance * 0.75).toFixed(2) : '-';

  const lastBetValue = apostas.length > 0 && apostas[0] ? (
    apostas[0].status === 'pending' ? '-' :
    apostas[0].status === 'won' ? '↑' + (apostas[0].potentialWinnings - apostas[0].amount).toFixed(2) + '€' :
    '↓' + apostas[0].amount.toFixed(2) + '€'
  ) : 'N/A';

  const sportIcons = {
    futebol: FaFutbol,
    basket: FaBasketball,
    voleibol: FaVolleyball,
    andebol: MdOutlineSportsHandball,
    hoquei: GiHockey,
    tenis: IoTennisball,
    futebolAmericano: IoIosFootball,
    mma: MdSportsMma,
    cs2: SiCounterstrike,
    boxe: PiBoxingGlove,
    futsal: PiSoccerBallDuotone,
    outros: CgMoreO,
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'won':
        return { 
          content: (
            <>
              <FaCheck className="status-icon" />
              <span>Ganho</span>
            </>
          ), 
          class: 'badge-won' 
        };
      case 'lost':
        return { 
          content: (
            <>
              <FaTimes className="status-icon" />
              <span>Perdido</span>
            </>
          ), 
          class: 'badge-lost' 
        };
      case 'pending':
        return { content: <span>Pendente</span>, class: 'badge-pending' };
      default:
        return { content: <span>Desconhecido</span>, class: 'badge-default' };
    }
  };

  const header = (actions) => (
    <PageHeader
      eyebrow="Desafio"
      title="Histórico"
      subtitle="Todas as apostas do desafio, da mais recente para a mais antiga."
      actions={actions}
    />
  );

  if (loading) return (
    <div className="page-bg-overlay">
      <main className="page-container historico-page-container">
        {header()}
        <SkeletonBetList />
      </main>
    </div>
  );
  if (error) return (
    <div className="page-bg-overlay">
      <main className="page-container historico-page-container">
        {header()}
        <div className="error-state">Erro ao carregar o histórico: {error}</div>
      </main>
    </div>
  );

  return (
    <div className="page-bg-overlay">
      <main className="page-container historico-page-container">
        <section className="historico-section">
          {header(
            <button className="btn historico-link" onClick={() => setShowStatsModal(true)} title="Ver estatísticas">
              <FaChartLine />
              <span>Estatísticas</span>
            </button>
          )}

          <div className="historico-stats">
            <div className="stat-item">
              <span className="stat-label">Saldo Atual</span>
              <span className="stat-value">{balance.toFixed(2)}€</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">
                Saldo Adicionado 
                <span
                  className="info-icon-wrapper"
                  tabIndex={0}
                  aria-label="Saldo adicionado quando o desafio é perdido e o saldo atual fica inferior a 10€"
                  data-tooltip="Saldo adicionado quando o desafio é perdido e o saldo atual fica inferior a 10€"
                >
                  <FaInfoCircle className="info-icon" />
                </span>
              </span>
              <span className="stat-value">{totalAdded.toFixed(2)}€</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Última Aposta</span>
              <span className={`stat-value ${apostas.length > 0 && apostas[0] && apostas[0].status === 'won' ? 'highlight-green' : apostas[0] && apostas[0].status === 'lost' ? 'highlight-red' : ''}`}>
                {lastBetValue}
              </span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Próxima Aposta</span>
              <span className="stat-value">{nextBet}{nextBet !== '-' ? '€' : ''}</span>
            </div>
          </div>

          <div className={`apostas-list ${isAnimating ? 'fade-in' : ''}`}>
            {paginatedApostas.map((aposta) => {
              const statusBadge = getStatusBadge(aposta.status);
              return (
                <div key={aposta.id} className="aposta-card">
                  <div className="aposta-header">
                    <div className="aposta-info">
                      <h3 className="aposta-date">{formatDate(aposta.date)}</h3>
                      <p className="aposta-type">{aposta.type} ({aposta.gamesCount})</p>
                    </div>
                    <div className={`aposta-status ${statusBadge.class}`}>
                      {statusBadge.content}
                    </div>
                  </div>

                  <div className="aposta-games">
                    {aposta.games.map((game, index) => {
                      const SportIcon = sportIcons[game.sport];
                      return (
                        <div key={index} className="game-row">
                          <div className="game-icon">
                            {SportIcon && <SportIcon className="sport-icon" style={{ color: sportColors[game.sport] }} />}
                          </div>
                          <div className="game-content">
                            <span className="game-pick">{game.pick}</span>
                            <span className="game-teams">{game.homeTeam}{game.awayTeam ? ` vs ${game.awayTeam}` : ''}</span>
                          </div>
                          <span className="game-odd">{game.odd}</span>
                        </div>
                      );
                    })}
                  </div>

                  <div className="aposta-footer">
                    <div className="aposta-stat">
                      <span className="stat-label">Odd Total</span>
                      <span className="stat-value">{aposta.totalOdd.toFixed(2)}</span>
                    </div>
                    <div className="aposta-stat">
                      <span className="stat-label">Valor Apostado</span>
                      <span className="stat-value">{aposta.amount.toFixed(2)}€</span>
                    </div>
                    <div className={`aposta-stat ${aposta.status === 'won' ? 'highlight-green' : aposta.status === 'lost' ? 'highlight-red' : 'highlight'}`}>
                      <span className="stat-label">Ganhos Possíveis</span>
                      <span className="stat-value">{aposta.potentialWinnings.toFixed(2)}€</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button 
                className="pagination-btn" 
                onClick={() => setCurrentPage(currentPage - 1)} 
                disabled={currentPage === 1}
                title="Página anterior"
              >
                <FaAngleLeft />
              </button>
              <span className="pagination-info">
                Página {currentPage} de {totalPages}
              </span>
              <button 
                className="pagination-btn" 
                onClick={() => setCurrentPage(currentPage + 1)} 
                disabled={currentPage === totalPages}
                title="Próxima página"
              >
                <FaAngleRight />
              </button>
            </div>
          )}
        </section>
      </main>
      {showStatsModal && <StatsModal chartData={chartData} wonCount={wonCount} lostCount={lostCount} balance={balance} onClose={() => setShowStatsModal(false)} />}
    </div>
  );
}

function StatsModal({ chartData, wonCount, lostCount, balance, onClose }) {
  return ReactDOM.createPortal(
    <div className="stats-modal-overlay" onClick={onClose}>
      <div className="stats-modal" onClick={(e) => e.stopPropagation()}>
        <header className="stats-modal-header">
          <h3>Estatísticas</h3>
          <button className="stats-modal-close" onClick={onClose} aria-label="Fechar">×</button>
        </header>
        <div className="stats-modal-body">
          <div className="stats-summary">
            <div className="stat-item">
              <span className="stat-label">Vitórias</span>
              <span className="stat-value highlight-green">{wonCount}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Derrotas</span>
              <span className="stat-value highlight-red">{lostCount}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Valor Inicial</span>
              <span className="stat-value">10.00€</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Saldo Atual</span>
              <span className="stat-value">{balance.toFixed(2)}€</span>
            </div>
          </div>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={360}>
              <LineChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                <XAxis
                  dataKey="date"
                  type="number"
                  scale="time"
                  domain={['dataMin', 'dataMax']}
                  tickFormatter={(value) => new Date(value).toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit' })}
                  tick={{ fill: '#71717a', fontSize: 12 }}
                  axisLine={{ stroke: 'rgba(255,255,255,0.12)' }}
                  tickLine={false}
                  minTickGap={24}
                />
                <YAxis
                  tick={{ fill: '#71717a', fontSize: 12 }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(value) => `${value}€`}
                  width={56}
                />
                <Tooltip
                  contentStyle={{ background: '#1b1b20', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, fontSize: 13 }}
                  labelStyle={{ color: '#a1a1aa' }}
                  itemStyle={{ color: '#f4f4f5' }}
                  cursor={{ stroke: 'rgba(255,255,255,0.2)' }}
                  labelFormatter={(value) => new Date(value).toLocaleDateString('pt-PT')}
                  formatter={(value) => [`${Number(value).toFixed(2)}€`, 'Saldo']}
                />
                <Line type="monotone" dataKey="balance" stroke="#9147ff" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <p className="stats-empty">Sem dados para exibir.</p>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

export default HistoricoPage;
